/**
 * Marker Icon Mapping Utility
 *
 * Maps MarkerIconType keys to Tabler icon components.
 * Uses centralized type system from types/MarkerIcon.ts for consistency.
 *
 * @module utils/markerIcons
 */

import type { TablerIcon } from '@tabler/icons-react';
import {
  IconRoad,
  IconBuildingBridge,
  IconTrafficCone,
  IconCamera,
  IconAlertTriangle,
  IconTool,
  IconBarrierBlock,
  IconMapPin,
  IconClipboard,
  IconRoadSign,
  IconExclamationCircle,
  IconEye,
  IconHammer,
} from '@tabler/icons-react';
import { MarkerIcon, type MarkerIconType } from '../types/MarkerIcon';

/**
 * Icon Mapping Registry
 *
 * Maps MarkerIconType keys to Tabler React icon components.
 * Single source of truth for icon rendering.
 */
const ICON_COMPONENT_MAP: Record<MarkerIconType, TablerIcon> = {
  // Transport
  [MarkerIcon.ROAD]: IconRoad,
  [MarkerIcon.LOCATION_MARKER]: IconMapPin,
  [MarkerIcon.BUILDING_BRIDGE]: IconBuildingBridge,

  // Safety
  [MarkerIcon.HAZARD_WARNING]: IconAlertTriangle,
  [MarkerIcon.BARRIER_BLOCK]: IconBarrierBlock,
  [MarkerIcon.TRAFFIC_CONTROL]: IconTrafficCone,

  // Construction
  [MarkerIcon.CONSTRUCTION]: IconHammer,
  [MarkerIcon.MAINTENANCE]: IconTool,

  // Inspection
  [MarkerIcon.INSPECTION]: IconCamera,
  [MarkerIcon.VISUAL_CHECK]: IconEye,
  [MarkerIcon.SURVEY_REPORT]: IconClipboard,
  [MarkerIcon.ROAD_SIGN]: IconRoadSign,
  [MarkerIcon.ISSUE_FOUND]: IconExclamationCircle,
};

/**
 * Get Tabler Icon Component
 *
 * Returns the Tabler React icon component for a given icon key.
 * Returns undefined if the icon key is not found (for graceful degradation).
 *
 * @param iconKey - The marker icon key
 * @returns Tabler icon component or undefined
 *
 * @example
 * ```typescript
 * const IconComponent = getTablerIcon(MarkerIcon.ROAD);
 * if (IconComponent) {
 *   return <IconComponent size={24} />;
 * }
 * ```
 */
export const getTablerIcon = (iconKey: string | undefined): TablerIcon | undefined => {
  if (!iconKey) return undefined;
  return ICON_COMPONENT_MAP[iconKey as MarkerIconType];
};
